import { Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import React, { useCallback, useMemo, useState } from 'react'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'

interface TabContentProps {
  children?: React.ReactNode
  tabIndex: number
  activeTabIndex: number
  tabContainerHeight?: string | number
}

export interface TabConfigurationProps {
  id: number
  title: string
  itemCount?: number
  componentProps: any
  shouldShowBadge?: boolean
  ContentComponent?: React.ComponentType<any>
}

interface TabbedContainerProps {
  tabConfigs: TabConfigurationProps[]
  shouldShowTabBar?: boolean
  ContentComponent?: React.ComponentType<any>
  showGlobalCountBadge?: boolean
  headerActions?: React.ReactNode[]
  onTabChange?: (tabIndex: number) => void
  currentActiveTab?: number
  tabContainerHeight?: string | number
  tabHeaderTopSpacing?: string
  isLargeTabHeader?: boolean
}

function TabContentPanel(props: TabContentProps) {
  const { children, activeTabIndex, tabIndex, tabContainerHeight, ...otherProps } = props
  const theme = useTheme()

  return (
    <Box
      role="tabpanel"
      hidden={activeTabIndex !== tabIndex}
      id={`tabpanel-${tabIndex}`}
      aria-labelledby={`tab-${tabIndex}`}
      {...otherProps}
    >
      {activeTabIndex === tabIndex && (
        <Box sx={{ p: 1, backgroundColor: getThemeColor(theme, MUIThemeColors.white), height: 'fit-content', maxHeight: tabContainerHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      )}
    </Box>
  )
}

function getAccessibilityProps(tabIndex: number) {
  return {
    id: `tab-${tabIndex}`,
    'aria-controls': `tabpanel-${tabIndex}`,
  }
}

const shouldDisplayCountBadge = (tabSpecificSetting?: boolean, globalSetting?: boolean): boolean => {
  return typeof tabSpecificSetting === 'boolean' ? tabSpecificSetting : !!globalSetting
}

const getValidTabIndex = (tabConfig: TabConfigurationProps, arrayIndex: number): number => {
  return tabConfig.id ?? arrayIndex
}

export const TabbedContainer: React.FC<TabbedContainerProps> = ({
  tabConfigs,
  shouldShowTabBar = true,
  ContentComponent,
  showGlobalCountBadge,
  headerActions,
  onTabChange,
  currentActiveTab,
  tabContainerHeight,
  isLargeTabHeader,
  tabHeaderTopSpacing = '4px',
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState(0)
  const activeTabIndex = currentActiveTab !== undefined ? currentActiveTab : internalActiveTab
  const theme = useTheme()

  const memoizedThemeColors = useMemo(
    () => ({
      background: getThemeColor(theme, MUIThemeColors.backgroundMain),
      text: getThemeColor(theme, MUIThemeColors.black),
      primary: getThemeColor(theme, MUIThemeColors.primaryMain),
    }),
    [theme],
  )

  const renderedTabElements = useMemo(
    () =>
      tabConfigs.map((tabConfig, arrayIndex) => {
        const validTabIndex = getValidTabIndex(tabConfig, arrayIndex)
        const shouldShowBadge = shouldDisplayCountBadge(tabConfig.shouldShowBadge, showGlobalCountBadge)
        const isActive = currentActiveTab == tabConfig.id
        return (
          <StyledTab
            key={validTabIndex}
            active={isActive}
            label={
              <Flex.Row>
                <Typography
                  color={
                    isActive
                      ? getThemeColor(theme, MUIThemeColors.primaryMain)
                      : getThemeColor(theme, MUIThemeColors.secondaryMain)
                  }
                  variant={isLargeTabHeader ? 'subtitle1' : 'body1'}
                  fontWeight={500}
                >
                  {tabConfig.title}
                  {typeof tabConfig.itemCount === 'number' && shouldShowBadge && (
                    <ItemCountBadge>{tabConfig.itemCount}</ItemCountBadge>
                  )}
                </Typography>
                {arrayIndex < tabConfigs.length - 1 && (
                  <Flex.Column justifyContent={'center'} paddingLeft={'10px'}>
                    <Box
                      sx={{
                        height: '18px',
                        width: '2px',
                        backgroundColor: isActive
                          ? getThemeColor(theme, MUIThemeColors.primaryMain)
                          : getThemeColor(theme, MUIThemeColors.secondaryMain),
                      }}
                    />
                  </Flex.Column>
                )}
              </Flex.Row>
            }
            {...getAccessibilityProps(validTabIndex)}
            tabcolor={memoizedThemeColors.primary}
            bgcolor={memoizedThemeColors.background}
          />
        )
      }),
    [tabConfigs, memoizedThemeColors, showGlobalCountBadge, currentActiveTab, isLargeTabHeader],
  )

  const renderTabContentPanels = useCallback(() => {
    return tabConfigs.map((tabConfig, arrayIndex) => {
      const validTabIndex = getValidTabIndex(tabConfig, arrayIndex)
      const TabContentComponent = tabConfig.ContentComponent
      return (
        <TabContentPanel
          activeTabIndex={activeTabIndex}
          key={validTabIndex}
          tabIndex={validTabIndex}
          tabContainerHeight={tabContainerHeight}
        >
          {ContentComponent ? (
            <ContentComponent {...tabConfig.componentProps} />
          ) : TabContentComponent ? (
            <TabContentComponent {...tabConfig.componentProps} />
          ) : null}
        </TabContentPanel>
      )
    })
  }, [tabConfigs, activeTabIndex, ContentComponent])

  const handleTabSelection = useCallback(
    (event: React.SyntheticEvent, newTabIndex: number) => {
      setInternalActiveTab(newTabIndex)
      onTabChange?.(newTabIndex)
    },
    [onTabChange],
  )

  if (!shouldShowTabBar) {
    const firstTabConfig = tabConfigs[0]
    const TabContentComponent = firstTabConfig?.ContentComponent
    return (
      <Box sx={{ width: '100%' }}>
        {headerActions && headerActions.length > 0 && (
          <HeaderActionsContainer>
            {headerActions.map((action, actionIndex) => (
              <span key={actionIndex}>{action}</span>
            ))}
          </HeaderActionsContainer>
        )}
        {firstTabConfig ? (
          ContentComponent ? (
            <ContentComponent {...firstTabConfig.componentProps} />
          ) : TabContentComponent ? (
            <TabContentComponent {...firstTabConfig.componentProps} />
          ) : null
        ) : null}
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TabBarContainer>
        <Tabs
          value={activeTabIndex}
          onChange={handleTabSelection}
          aria-label="content tabs"
          TabIndicatorProps={{ hidden: true }}
          sx={{ position: 'relative', top: tabHeaderTopSpacing }}
        >
          {renderedTabElements}
        </Tabs>
        {headerActions && headerActions.length > 0 && (
          <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
            {headerActions.map((action, actionIndex) => (
              <span key={actionIndex}>{action}</span>
            ))}
          </Box>
        )}
      </TabBarContainer>
      {renderTabContentPanels()}
    </Box>
  )
}

export default TabbedContainer

const StyledTab = styled(Tab)<{ tabcolor: string; bgcolor: string; active: boolean }>(
  ({ tabcolor, bgcolor, theme, active }) => ({
    whiteSpace: 'nowrap',
    textTransform: 'none',
    color: `${tabcolor} !important`,
    maxWidth: 'none',
    paddingLeft: '0px',
    paddingRight: '10px',
    ':hover': {
      bottom: !active ? '2px' : '0px',
    },
  }),
)

export const ItemCountBadge = styled('span')(({ theme }) => ({
  fontSize: '12px',
  backgroundColor: getThemeColor(theme, MUIThemeColors.primaryMain),
  color: getThemeColor(theme, MUIThemeColors.white),
  borderRadius: '4px',
  marginLeft: '6px',
  padding: '2px 6px',
}))

export const HeaderActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginBottom: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(1),
}))

export const TabBarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '36px',
  marginBottom: '5px',
}))
